import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flex, VerticalSpacing } from '@banhala/ui-kit';
import { GoodsInfoWebView } from './components/GoodsInfoWebView';
import { ContentContainer } from '../ContentContainer';
import { GoodsAtomicRenderer } from './components/AtomicRenderer';
import { RelatedOutfits } from './components/RelatedOutfits';
import { RelatedTags } from './components/RelatedTags';
import { LegalInfo } from './components/LegalInfo';
import { useElementViewportTracking } from '../../../../store/sectionVisibility';
import { BeautyBanner } from '../../../BeautyBanner';
import { useGoodsDetail } from '../../../../queries/useGoodsDetail';
import { isNotNil } from 'shared/src/utils';
import { useGoodsInstanceId } from '../../../../hooks/useGoodsInstanceId';

const BEAUTY_BANNER_HEIGHT = 50;

export const GoodsInfoTab: React.FC<{
  focused: boolean;
  onChangeTabHeight: (height: number) => void;
}> = props => {
  const onChangeTabHeightRef = useRef(props.onChangeTabHeight);
  onChangeTabHeightRef.current = props.onChangeTabHeight;
  const key = useGoodsInstanceId('atomic');

  const trackingRef = useElementViewportTracking(key, 0);

  const { data: goodsDetail } = useGoodsDetail();
  const showBeautyBanner = isNotNil(
    goodsDetail?.component_visibility.beauty_component?.banner,
  );

  const [height, setHeight] = useState({
    info: 1556,
    atomic: 794,
    legal: 152,
    codi: 0,
    tags: 0,
  });

  useEffect(() => {
    const nextHeight =
      Object.values<number>(height).reduce((prev, acc) => prev + acc, 0) +
      (showBeautyBanner ? BEAUTY_BANNER_HEIGHT : 0);
    onChangeTabHeightRef.current(nextHeight);
  }, [height, showBeautyBanner]);

  const handleInfoHeight = useCallback((h: number) => {
    setHeight(prev => {
      if (prev.info === h) return prev;
      return { ...prev, info: h };
    });
  }, []);

  const handleAtomicHeight = useCallback((h: number) => {
    setHeight(prev => {
      if (prev.atomic === h) return prev;
      return { ...prev, atomic: h };
    });
  }, []);

  const handleCodiHeight = useCallback((h: number) => {
    setHeight(prev => {
      if (prev.codi === h) return prev;
      return { ...prev, codi: h };
    });
  }, []);

  const handleTagsHeight = useCallback((h: number) => {
    setHeight(prev => {
      if (prev.tags === h) return prev;
      return { ...prev, tags: h };
    });
  }, []);

  const handleLegalHeight = useCallback((h: number) => {
    setHeight(prev => {
      if (prev.legal === h) return prev;
      return { ...prev, legal: h };
    });
  }, []);

  return (
    <ContentContainer focused={props.focused}>
      <Flex>
        <GoodsInfoWebView onChangeHeight={handleInfoHeight} />
        {showBeautyBanner && <BeautyBanner />}
        <Flex ref={trackingRef}>
          <GoodsAtomicRenderer
            screen={'GOODS_DETAIL'}
            focused={props.focused}
            onChangeHeight={handleAtomicHeight}
          />
        </Flex>
        <RelatedOutfits
          focused={props.focused}
          onChangeHeight={handleCodiHeight}
        />
        <RelatedTags onChangeHeight={handleTagsHeight} />
        <LegalInfo onChangeHeight={handleLegalHeight} />
        <VerticalSpacing size={36} />
      </Flex>
    </ContentContainer>
  );
};
